import {
  FormatterStyle,
  DATEFORMATS,
  WEEKDAYFORMAT,
  RangeDatePickerInputValue,
} from '../types';

export const nullFn = (): null => null;

export const formatDate = (
  date: Date | string,
  formatStyle: FormatterStyle = 'medium',
  locale = 'en-US',
): string => {
  if (date) {
    try {
      const options = DATEFORMATS[formatStyle] || DATEFORMATS.medium;
      const value = typeof date === 'string' ? new Date(date) : date;
      return new Intl.DateTimeFormat(locale, options).format(value);
    } catch (err) {
      return '';
    }
  }

  return '';
};

export function getYearsList(
  year?: number,
  lowerLimit = 10,
  upperLimit = 14,
): number[] {
  const yearsList = [];
  const y = year ? year : new Date().getFullYear();
  for (let i = y - lowerLimit; i < y + upperLimit; i++) {
    yearsList.push(i);
  }

  return yearsList;
}

export function getMonthList(
  year?: number,
  format: 'long' | 'short' = 'long',
  locale = 'en-US',
): string[] {
  const monthList = [...Array(12).keys()];
  const y = year ? year : new Date().getFullYear();
  const formatter = new Intl.DateTimeFormat(locale, {
    month: format,
  });

  const getMonthName = (monthIdx: number) =>
    formatter.format(new Date(y, monthIdx));

  return monthList.map(getMonthName);
}

export function normalizeDate(date?: Date | string): Date {
  if (date === undefined) return new Date();
  if (typeof date === 'string') return new Date(date);
  return date;
}

export function isEmptyRange<T>(range: T): boolean {
  if (Array.isArray(range) && range.length > 0) {
    return range.filter((item) => !!item).length === 0;
  }

  return true;
}

export function normalizeRange(range: Date[] | Date | undefined): Date[] {
  if (Array.isArray(range)) {
    return range.map((date: Date, idx: number) => {
      if (idx >= 1) {
        date.setHours(23, 59, 59, 999);
      }
      return normalizeDate(date);
    });
  }
  if (range) return [normalizeDate(range)];
  return [];
}

export function getFormattedDayName(
  date: Date,
  format: WEEKDAYFORMAT = 'short',
  locale = 'en-US',
): string {
  try {
    return new Intl.DateTimeFormat(locale, { weekday: format }).format(date);
  } catch (err) {
    return '';
  }
}

export function addDaysToCalendar(date: Date | string, days: number) {
  const ret = new Date(date);
  ret.setDate(ret.getDate() + days);
  return new Date(ret);
}

export function setMonth(month: number, date?: Date | string): Date {
  date = date ? (typeof date === 'string' ? new Date(date) : date) : new Date();
  const d = date ? new Date(date) : new Date();
  d.setMonth(month);
  return d;
}

export function addMonth(months: number, date?: Date | string) {
  date = date ? (typeof date === 'string' ? new Date(date) : date) : new Date();
  const d = date ? new Date(date) : new Date();

  d.setMonth(date.getMonth() + months);
  return d;
}

export function addYear(year: number, date?: Date | string) {
  date = date ? (typeof date === 'string' ? new Date(date) : date) : new Date();
  const d = date ? new Date(date) : new Date();
  d.setFullYear(year);
  return d;
}

export function getFirstDayOfMonth(value?: Date | string): Date {
  const d = value ? new Date(value) : new Date();
  const isValid = !isNaN(d.getTime());
  if (isValid) {
    return firstOfMonth(d);
  }

  return firstOfMonth(new Date());
}

function firstOfMonth(value: Date): Date {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  d.setDate(1);
  return d;
}

export function getLastDayOfMonth(value: Date | string): Date {
  const date = new Date(value);
  date.setMonth(date.getMonth() + 1);
  date.setDate(1);
  date.setDate(date.getDate() - 1);
  return date;
}

export function compareDates(a?: Date, b?: Date): number {
  const aValue = a ? new Date(a).setHours(0, 0, 0, 0) : undefined;
  const bValue = b ? new Date(b).setHours(0, 0, 0, 0) : undefined;
  return getSign((aValue || 0) - (bValue || 0));
}

function getSign(value?: number): number {
  // eslint-disable-next-line no-compare-neg-zero
  if (!value || value === 0 || value === -0) return 0;
  return Math.abs(value) / value;
}

export function isEqualDate(a?: Date | string, b?: Date | string): boolean {
  if (a === undefined || b === undefined) return false;
  try {
    const aValue = typeof a === 'string' ? new Date(a) : a;
    const bValue = typeof b === 'string' ? new Date(b) : b;
    return !!(
      aValue &&
      bValue &&
      aValue.getDate() === bValue.getDate() &&
      aValue.getMonth() === bValue.getMonth() &&
      aValue.getFullYear() === bValue.getFullYear()
    );
  } catch (err) {
    return false;
  }
}

export function isDateWithinRange(date?: Date, range?: Date[]): boolean {
  if (date && Array.isArray(range) && range.length > 1) {
    const [rangeStart, rangeEnd] = range;
    return (
      compareDates(date, rangeStart) >= 0 && compareDates(date, rangeEnd) <= 0
    );
  }
  return false;
}

type RangeReturnType =
  | {
      range: Date[];
    }
  | undefined;

export function buildNewRangeFromValue(
  value?: Date,
  currentRange?: RangeDatePickerInputValue,
): RangeReturnType {
  if (!value) return undefined;
  if (currentRange && currentRange.length > 0) {
    const [rangeStart, rangeEnd] = currentRange;
    const newRangeStart = new Date(rangeStart);
    newRangeStart.setHours(0, 0, 0, 0);

    if (!rangeEnd) {
      if (compareDates(value, newRangeStart) <= 0) {
        value.setHours(0, 0, 0, 0);
        return {
          range: [value],
        };
      }

      value.setHours(23, 59, 59, 999);
      return {
        range: [newRangeStart, value],
      };
    }
  }

  value.setHours(0, 0, 0, 0);
  return {
    range: [value],
  };
}

export function shouldDateBeSelected(
  date?: Date,
  currentValue?: Date | string,
  selectionType?: string,
  currentRange?: Date[],
) {
  if (!date || !currentValue) return false;
  if (selectionType === 'single') return isEqualDate(date, currentValue);
  if (Array.isArray(currentRange) && currentRange.length > 0) {
    const [rangeStart, rangeEnd] = currentRange;
    return isEqualDate(date, rangeStart) || isEqualDate(date, rangeEnd);
  }
  return false;
}

export function getFormattedMonth(value: Date, locale = 'en-US'): string {
  try {
    return new Intl.DateTimeFormat(locale, { month: 'long' }).format(value);
  } catch (err) {
    return '';
  }
}

type getYearsRangeTypes = {
  minValue?: Date;
  maxValue?: Date;
  currentMonth: number;
};

type isDisabledTypes = {
  minValue?: Date;
  maxValue?: Date;
  currentMonth: number;
  year: number;
};

function isDisabled({
  minValue,
  maxValue,
  currentMonth,
  year,
}: isDisabledTypes) {
  if (
    maxValue &&
    maxValue.getFullYear() === year &&
    maxValue.getMonth() < currentMonth
  ) {
    return true;
  }
  if (
    minValue &&
    minValue.getFullYear() === year &&
    minValue.getMonth() > currentMonth
  ) {
    return true;
  }

  return false;
}

export function getYearsRange({
  minValue,
  maxValue,
  currentMonth,
}: getYearsRangeTypes) {
  const todayYear = new Date().getFullYear();
  let from;
  let to;
  if (minValue) {
    from = new Date(minValue).getFullYear();
  } else {
    from = new Date(todayYear - 100, 0, 1).getFullYear();
  }

  if (maxValue && maxValue > new Date(from)) {
    to = new Date(maxValue).getFullYear();
  } else {
    to = new Date(todayYear + 100, 0, 1).getFullYear();
  }

  const range = [];
  for (let i = from; i <= to; i += 1) {
    range.push({
      value: i,
      label: i,
      disabled: isDisabled({ minValue, maxValue, currentMonth, year: i }),
    });
  }

  return range;
}

export function isEqualMonth(
  date1?: Date | string,
  date2?: Date | string,
): boolean {
  try {
    const v1 = typeof date1 === 'string' ? new Date(date1) : date1;
    const v2 = typeof date2 === 'string' ? new Date(date2) : date2;
    return !!(v1 && v2 && v1.getMonth() === v2.getMonth());
  } catch (err) {
    return false;
  }
}

export function isEqualYear(
  date1?: Date | string,
  date2?: Date | string,
): boolean {
  try {
    const v1 = typeof date1 === 'string' ? new Date(date1) : date1;
    const v2 = typeof date2 === 'string' ? new Date(date2) : date2;
    return !!(v1 && v2 && v1.getFullYear() === v2.getFullYear());
  } catch (err) {
    return false;
  }
}

export function getNextFocusedDate(
  currentDate?: Date | string,
  nextDate?: Date | string,
): Date {
  if (!currentDate || !nextDate) return getFirstDayOfMonth(new Date());

  return isEqualMonth(currentDate, nextDate) &&
    isEqualYear(currentDate, nextDate)
    ? typeof currentDate === 'string'
      ? new Date(currentDate)
      : currentDate
    : getFirstDayOfMonth(nextDate);
}
